import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.address.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    if (formData.message.length > 500) {
      setError('Message must be 500 characters or less.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('address', formData.address);
      body.append('message', formData.message);
      const res = await fetch('https://readdy.ai/api/form/d6aj13tbiueuoqm1shig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', address: '', message: '' });
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-black text-white">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          {/* Left Side - Links */}
          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-10">
            {/* Quick Links Column */}
            <div>
              <h3 className="text-lg font-semibold text-yellow-500 mb-6">Quick Links</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/" className="text-sm text-gray-400 hover:text-yellow-400 transition-colors">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/get-cash" className="text-sm text-gray-400 hover:text-yellow-400 transition-colors">
                    Blog
                  </Link>
                </li>
                <li>
                  <Link to="/get-cash/market-data-el-paso-texas" className="text-sm text-gray-400 hover:text-yellow-400 transition-colors">
                    El Paso Real Estate Market
                  </Link>
                </li>
                <li>
                  <Link to="/get-cash/el-paso-neighborhood-guide-sellers" className="text-sm text-gray-400 hover:text-yellow-400 transition-colors">
                    Neighborhood Guide
                  </Link>
                </li>
              </ul>
            </div>

            {/* Resources Column */}
            <div>
              <h3 className="text-lg font-semibold text-yellow-500 mb-6">Resources</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/get-cash/sell-house-fast-el-paso" className="text-sm text-gray-400 hover:text-yellow-400 transition-colors">
                    Sell Fast
                  </Link>
                </li>
                <li>
                  <Link to="/get-cash/sell-house-as-is-el-paso" className="text-sm text-gray-400 hover:text-yellow-400 transition-colors">
                    Sell As-Is
                  </Link>
                </li>
                <li>
                  <Link to="/get-cash/el-paso-closing-costs-seller-fees" className="text-sm text-gray-400 hover:text-yellow-400 transition-colors">
                    Closing Costs
                  </Link>
                </li>
                <li>
                  <Link to="/get-cash/avoiding-foreclosure-options" className="text-sm text-gray-400 hover:text-yellow-400 transition-colors">
                    Avoid Foreclosure
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="sm:col-span-2 mt-4">
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <span className="w-5 h-5 flex items-center justify-center text-yellow-500"><i className="ri-map-pin-line"></i></span>
                El Paso, Texas
              </div>
            </div>
          </div>

          {/* Right Side - Seller Inquiry Form */}
          <div className="lg:col-span-7">
            <h3 className="text-lg font-semibold text-yellow-500 mb-2">Get Your Free Cash Offer</h3>
            <p className="text-sm text-gray-400 mb-5">Tell us about your property and we&apos;ll respond within 24 hours.</p>

            {submitted ? (
              <div className="bg-green-900/40 border border-green-700/50 rounded-lg p-6 text-center">
                <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3 text-green-400 text-2xl"><i className="ri-checkbox-circle-line"></i></span>
                <p className="text-white font-medium mb-1">Thank you!</p>
                <p className="text-sm text-gray-400">We&apos;ve received your inquiry and will be in touch soon.</p>
              </div>
            ) : (
              <form
                id="seller-inquiry-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-3"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    name="name"
                    placeholder="Full Name *"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-white/10 border border-white/15 rounded-md px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500/60 transition-colors"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-white/10 border border-white/15 rounded-md px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500/60 transition-colors"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number *"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-white/10 border border-white/15 rounded-md px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500/60 transition-colors"
                  />
                  <input
                    type="text"
                    name="address"
                    placeholder="Property Address *"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full bg-white/10 border border-white/15 rounded-md px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500/60 transition-colors"
                  />
                </div>
                <textarea
                  name="message"
                  placeholder="Tell us about your situation (optional)"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={3}
                  className="w-full bg-white/10 border border-white/15 rounded-md px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500/60 transition-colors resize-none"
                />
                {error && (
                  <p className="text-red-400 text-xs">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="whitespace-nowrap cursor-pointer bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-md px-6 py-2.5 text-sm transition-colors disabled:opacity-60"
                >
                  {submitting ? 'Sending...' : 'Get My Cash Offer'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 text-center">
          <p className="text-xs text-gray-500">
            © 2025 El Paso Cash Home Buyers. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
